import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";

import { FiltersScopeProvider } from "~/components/filters/FiltersScope";
import { defaultActivityFilter, getDefaultEventSort } from "~/constants/filters";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "~/constants/pagination";
import { EventSortByEnum } from "~/types/event";
import type { IEventFilters } from "~/types/eventFilters";
import { SortOrderEnum } from "~/types/sort";
import { deepEqual } from "~/utils/deepEqual";
import { normalizeFilterValue } from "~/utils/filters/filterAllOptions";
import { getDashboardTabFromPathname } from "~/utils/getDashboardTabFromPathname";

interface IMarketSettings {
  ungroup: boolean;
}

export type MarketsSettingsState = {
  [K in keyof IMarketSettings]?: IMarketSettings[K];
};

export interface DashboardSortState {
  sortBy?: EventSortByEnum;
  sortOrder?: SortOrderEnum;
}

export interface DashboardPaginationState {
  page: number;
  limit: number;
}

export type DashboardFiltersState = {
  [K in keyof IEventFilters]?: IEventFilters[K];
};

interface IFilterContextValue {
  sort: DashboardSortState;
  defaultSort: DashboardSortState;
  setSort: (patch: Partial<DashboardSortState>) => void;
  pagination: DashboardPaginationState;
  filters: DashboardFiltersState;
  updateFilters: (patch: DashboardFiltersState) => void;
  setAllFilters: (value: DashboardFiltersState) => void;
  clearAllFilters: () => void;
  isFiltersApplied: boolean;
  activeFiltersCount: number;
  activeMoreFiltersCount: number;
  isWatchlistOnly: boolean;
  setWatchlistOnly: (value: boolean) => void;
  setPagination: (patch: Partial<DashboardPaginationState>) => void;
  marketsSettings: MarketsSettingsState;
  setMarketSettings: <K extends keyof IMarketSettings>(
    key: K,
    value: IMarketSettings[K]
  ) => void;
  registerOnClearAllFilters: (cb: () => void) => () => void;
  registerOnDashboardHomeReset: (cb: () => void) => () => void;
  triggerDashboardHomeReset: () => void;
  skipNextNavigationReset: () => void;
  consumeSkipNextNavigationReset: () => boolean;
  isDataLoading: boolean;
  setIsDataLoading: (value: boolean) => void;
}

const defaultMarketsSettings: MarketsSettingsState = {
  ungroup: false,
};

// One entry per filter chip; a chip counts as active when any of its keys
// differs from the default filter state. Toolbar chips stay visible, the
// rest live in the More filters panel.
const toolbarFilterGroups: (keyof IEventFilters)[][] = [
  ["excludeCategoryIds"],
  ["platforms"],
  ["endsIn"],
];

const moreFilterGroups: (keyof IEventFilters)[][] = [
  ["active", "resolved"],
  [
    "oneMarket",
    "hasDisputedMarkets",
    "minEventCount",
    "hasBreakingMarket",
    "hasTrendingMarket",
  ],
  ["minChance", "maxChance"],
  ["minSpread"],
  ["minLiquidity", "maxLiquidity"],
  ["minVolume", "maxVolume"],
  ["createdAt"],
  ["minVolume24h", "minPriceChange24h", "maxPriceChange24h"],
  ["apyFilter"],
  ["roiFilter"],
];

const countActiveFilterGroups = (
  filters: DashboardFiltersState,
  groups: (keyof IEventFilters)[][]
) =>
  groups.filter((group) =>
    group.some(
      (key) =>
        !deepEqual(
          normalizeFilterValue(key, filters[key]),
          normalizeFilterValue(key, defaultActivityFilter[key])
        )
    )
  ).length;

const toEffectiveFilters = (filters: DashboardFiltersState) =>
  Object.fromEntries(
    (Object.entries(filters) as [keyof IEventFilters, unknown][])
      .map(([key, value]) => [key, normalizeFilterValue(key, value)])
      .filter(([, value]) => value !== undefined)
  );

const defaultPagination: DashboardPaginationState = {
  page: DEFAULT_PAGE,
  limit: DEFAULT_PAGE_SIZE,
};

const isChosenSort = (
  sort: DashboardSortState | null
): sort is DashboardSortState => !!sort?.sortBy && !!sort.sortOrder;

const FilterContext = createContext<IFilterContextValue | null>(null);

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const defaultSort = getDefaultEventSort(
    getDashboardTabFromPathname(pathname)
  );
  const [filters, setFiltersState] = useState<DashboardFiltersState>(
    defaultActivityFilter
  );
  const [chosenSort, setChosenSort] = useState<DashboardSortState | null>(
    null
  );
  const sort = isChosenSort(chosenSort) ? chosenSort : defaultSort;
  const [pagination, setPaginationState] =
    useState<DashboardPaginationState>(defaultPagination);
  const [marketsSettings, setMarketsSettingsState] =
    useState<MarketsSettingsState>(defaultMarketsSettings);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isWatchlistOnly, setIsWatchlistOnly] = useState(false);

  const onClearAllFiltersCallbacks = useRef<Set<() => void>>(new Set());
  const onDashboardHomeResetCallbacks = useRef<Set<() => void>>(new Set());

  const registerOnClearAllFilters = useCallback((cb: () => void) => {
    const callbacks = onClearAllFiltersCallbacks.current;
    callbacks.add(cb);
    return () => {
      callbacks.delete(cb);
    };
  }, []);

  const registerOnDashboardHomeReset = useCallback((cb: () => void) => {
    const callbacks = onDashboardHomeResetCallbacks.current;
    callbacks.add(cb);
    return () => {
      callbacks.delete(cb);
    };
  }, []);

  const triggerDashboardHomeReset = useCallback(() => {
    onDashboardHomeResetCallbacks.current.forEach((cb) => cb());
  }, []);

  const skipNextNavigationResetRef = useRef(false);

  const skipNextNavigationReset = useCallback(() => {
    skipNextNavigationResetRef.current = true;
  }, []);

  const consumeSkipNextNavigationReset = useCallback(() => {
    if (skipNextNavigationResetRef.current) {
      skipNextNavigationResetRef.current = false;
      return true;
    }
    return false;
  }, []);

  const resetPage = useCallback(() => {
    setPaginationState((prev) => {
      if (prev.page === DEFAULT_PAGE) {
        return prev;
      }

      return { ...prev, page: DEFAULT_PAGE };
    });
  }, []);

  const setAllFilters = useCallback(
    (value: DashboardFiltersState) => {
      setFiltersState(value);
      resetPage();
    },
    [resetPage]
  );

  const setWatchlistOnly = useCallback(
    (value: boolean) => {
      setIsWatchlistOnly(value);
      resetPage();
    },
    [resetPage]
  );

  const updateFilters = useCallback(
    (patch: DashboardFiltersState) => {
      const next = { ...filters, ...patch };

      if (deepEqual(toEffectiveFilters(filters), toEffectiveFilters(next))) {
        return;
      }

      setFiltersState(next);
      resetPage();
    },
    [filters, resetPage]
  );

  const setMarketSettings = useCallback(
    <K extends keyof IMarketSettings>(
      key: K,
      value: IMarketSettings[K] | undefined
    ) => {
      setMarketsSettingsState((prev) => {
        const next = { ...prev };
        if (value === undefined) {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    []
  );

  const clearAllFilters = useCallback(() => {
    setFiltersState({ ...defaultActivityFilter });
    setChosenSort(null);
    setIsWatchlistOnly(false);
    resetPage();
    onClearAllFiltersCallbacks.current.forEach((cb) => cb());
  }, [resetPage]);

  const setSort = useCallback(
    (patch: Partial<DashboardSortState>) => {
      setChosenSort((prev) => ({ ...prev, ...patch }));
      resetPage();
    },
    [resetPage]
  );

  const setPagination = useCallback(
    (patch: Partial<DashboardPaginationState>) => {
      setPaginationState((prev) => {
        const next = { ...prev, ...patch };

        if (prev.page === next.page && prev.limit === next.limit) {
          return prev;
        }

        return next;
      });
    },
    []
  );

  const isFiltersApplied = useMemo(() => {
    const filteredAll = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined)
    );
    const filteredDefault = Object.fromEntries(
      Object.entries(defaultActivityFilter).filter(
        ([, value]) => value !== undefined
      )
    );
    return isWatchlistOnly || !deepEqual(filteredAll, filteredDefault);
  }, [filters, isWatchlistOnly]);

  const activeMoreFiltersCount = useMemo(
    () => countActiveFilterGroups(filters, moreFilterGroups),
    [filters]
  );

  const activeFiltersCount = useMemo(
    () =>
      countActiveFilterGroups(filters, toolbarFilterGroups) +
      activeMoreFiltersCount,
    [activeMoreFiltersCount, filters]
  );

  const value = useMemo(
    () => ({
      filters,
      pagination,
      setPagination,
      sort,
      defaultSort,
      setSort,
      setAllFilters,
      updateFilters,
      clearAllFilters,
      isFiltersApplied,
      activeFiltersCount,
      activeMoreFiltersCount,
      isWatchlistOnly,
      setWatchlistOnly,
      marketsSettings,
      setMarketSettings,
      registerOnClearAllFilters,
      registerOnDashboardHomeReset,
      triggerDashboardHomeReset,
      skipNextNavigationReset,
      consumeSkipNextNavigationReset,
      isDataLoading,
      setIsDataLoading,
    }),
    [
      filters,
      pagination,
      setPagination,
      sort,
      defaultSort,
      setSort,
      setAllFilters,
      updateFilters,
      clearAllFilters,
      isFiltersApplied,
      activeFiltersCount,
      activeMoreFiltersCount,
      isWatchlistOnly,
      setWatchlistOnly,
      marketsSettings,
      setMarketSettings,
      registerOnClearAllFilters,
      registerOnDashboardHomeReset,
      triggerDashboardHomeReset,
      skipNextNavigationReset,
      consumeSkipNextNavigationReset,
      isDataLoading,
    ]
  );

  return (
    <FilterContext.Provider value={value}>
      <FiltersScopeProvider value={value}>{children}</FiltersScopeProvider>
    </FilterContext.Provider>
  );
};

export const useFilter = (): IFilterContextValue => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within FilterProvider");
  }
  return context;
};
